// ────────────────────────────────────────────────────────────
// Workspace selector — shared types + localStorage helpers
// ────────────────────────────────────────────────────────────
// `timberline.lastWorkspace` drives RootGate's auto-redirect.
// `timberline.lastWorkspaceAt` (ISO timestamp) feeds the
// "last opened …" line on the Selector page; both are written
// together on every pick.

export type Workspace = 'deal-desk' | 'mi';

const KEY_WORKSPACE = 'timberline.lastWorkspace';
const KEY_AT        = 'timberline.lastWorkspaceAt';

export const WORKSPACE_LABEL: Record<Workspace, string> = {
  'deal-desk': 'Deal Desk',
  mi:          'Mineral Intelligence',
};

export const WORKSPACE_PATH: Record<Workspace, string> = {
  'deal-desk': '/deal-desk',
  mi:          '/mi/aois',
};

export function readLastWorkspace(): Workspace | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(KEY_WORKSPACE);
  // A stored 'workbench' from before the retirement simply falls through
  // to the Selector — no redirect loop, no migration needed.
  return v === 'deal-desk' || v === 'mi' ? v : null;
}

export function readLastWorkspaceAt(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(KEY_AT);
}

export function pickWorkspace(ws: Workspace): void {
  localStorage.setItem(KEY_WORKSPACE, ws);
  localStorage.setItem(KEY_AT, new Date().toISOString());
}

// Lowercase relative-time string. Past 30 days = "long time ago" because
// the exact date stops being useful context and becomes visual noise.
export function relativeTimeLowercase(iso: string | null): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const now  = Date.now();
  const ms   = now - then;
  if (ms < 0) return 'today';
  const dayMs = 86400_000;
  // Calendar-day diff: floor by local midnight so "yesterday" survives a
  // sub-24h gap that crosses midnight.
  const startOfToday = new Date(); startOfToday.setHours(0,0,0,0);
  const startOfThen  = new Date(then); startOfThen.setHours(0,0,0,0);
  const days = Math.round((startOfToday.getTime() - startOfThen.getTime()) / dayMs);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days <= 30) return `${days} days ago`;
  return 'long time ago';
}
